import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  API,
  ChainedClientCullAtomDTO,
  ChainedContentCullAtomDTO,
  ChainedPlayerCullAtomDTO,
  ClientCullDTO,
  ClientIdentityReturningPathBasedStructureCullAtomDTO,
  CompanyDTO,
  ContentCullDTO,
  ContentPathBasedStructureCullAtomDTO,
  CullCategoryDTO,
  CullWithAtomsDTO,
  GeoCullDTO,
  PlayerCullDTO,
  PlayerPathBasedStructureCullAtomDTO,
  PolygonDTO,
  TimeCullDTO,
} from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { SbModalState } from '../../../services/gui/modal/modal-state.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { Observable } from 'rxjs';
import { CompaniesComponent } from '../../company/companies/companies.component';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@ngx-seatback/utils';
import { ModalNavigationService } from '../../../services/gui/modal/modal-navigation.service';
import { PermissionService } from '../../../services/permission.service';
import { CullCategoriesComponent } from '../filter-categories/cull-categories.component';
import { CullCategoryEditComponent } from '../filter-categories-edit/cull-category-edit.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { ColorPickerService } from '../../../services/color-picker.service';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';

@Component({
  selector: 'filter-edit',
  templateUrl: './filter-edit.component.html',
  styleUrls: ['./filter-edit.component.scss'],
  standalone: false,
})
export class FilterEditComponent implements OnInit {
  public GEOCULLDONE: boolean = false;

  loading = true;

  @Input() cullId = undefined;

  @Input() hidden = false;
  @Input() type: string;
  @Input() isInModalContext: any = null;
  @Input() isInSidebarContext = false;

  companyId?: number;

  expressionValid: boolean = false;

  easyMode: boolean = true;
  currentTab: number = 1;

  // tslint:disable-next-line:variable-name
  _cull: CullWithAtomsDTO;
  nextDisable: boolean = false;
  debounceTimer: ReturnType<typeof setTimeout>;
  companyDisabled: boolean;
  polygonsToRender: PolygonDTO[];
  // color variables
  showColorPicker: boolean;
  color: string;

  @Input()
  set cull(value: CullWithAtomsDTO) {
    this._cull = value;
    if (this._cull) {
      this.expressionValid = this.isCullExpressionValid();
    } else {
      this.expressionValid = false;
    }

    this.previewCull = this._cull;

    setTimeout(() => {
      this.nextDisable = this.nextDisabled();
    }, 100);
  }

  get cull(): CullWithAtomsDTO {
    return this._cull;
  }

  // tslint:disable-next-line:variable-name
  _previewCull: CullWithAtomsDTO;

  get previewCull(): CullWithAtomsDTO {
    return this._previewCull;
  }

  set previewCull(value: CullWithAtomsDTO) {
    this._previewCull = value;
  }

  @Output() submitSuccess = new EventEmitter();

  form = new UntypedFormGroup({
    Name: new UntypedFormControl('', [Validators.required]),
    Type: new UntypedFormControl('', [Validators.required]),
    Hidden: new UntypedFormControl(''),
  });

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public navService: NavigationService,
    public modal: ModalService,
    private modalState: SbModalState,
    public util: UtilService,
    public translate: TranslateService,
    private modalNavigation: ModalNavigationService,
    public permissionService: PermissionService,
    private destroyRef: DestroyRef,
    public colorService: ColorPickerService
  ) {
    this.showColorPicker = false;
    this.polygonsToRender = [];
    this.type = null;
    this.companyDisabled = false;
    this.debounceTimer = null;
    this.form = new FormGroup({
      Name: new FormControl('', [Validators.required]),
      Type: new FormControl('', [Validators.required]),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get Type() {
    return this.form.get('Type');
  }

  nameValidator(): ValidatorFn {
    return (group: FormGroup): ValidationErrors => {
      const nameControl = group.get('Name');
      const typeControl = group.get('Type');
      if (!nameControl || !typeControl) {
        return null;
      }
      if (nameControl.value?.trim()?.length > 0 && typeControl.value) {
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
          this.api
            .cs_Cull_checkNameAvailability({
              type: encodeURIComponent(typeControl.value),
              name: encodeURIComponent(nameControl.value.trim()),
            })
            .subscribe({
              next: (result: boolean) => {
                if (result) {
                  nameControl.setErrors(null);
                } else {
                  nameControl.setErrors({ unique: true });
                }
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }, 500);
      } else {
        return null;
      }
    };
  }

  ngOnInit(): void {
    this.loading = true;
    let payload: any;
    if (this.isInModalContext) {
      if (this.modalState.options.get(this.isInModalContext)?.payload) {
        payload = this.modalState.options.get(this.isInModalContext).payload;
        this.cullId = payload;
      }
      if (this.modalNavigation.getCurrentNavigation().data?.cullType) {
        payload = this.modalNavigation.getCurrentNavigation().data.cullType;
        this.type = payload;
      }
      if (this.modalNavigation.getCurrentNavigation().data?.companyDisabled) {
        this.companyDisabled = this.modalNavigation.getCurrentNavigation().data.companyDisabled;
      }
    }
    if (this.cull?.id || this.type) {
      this.Type.disable();
    }
    if (this.cullId) {
      this.refreshFromServer();
    } else {
      this.activatedRoute.params.subscribe(params => {
        this.cullId = params.filterID;
        if (isNaN(this.cullId)) {
          this.cullId = undefined;
        }
        this.refreshFromServer();
      });
    }
  }

  nextDisabled() {
    // TODO refactor
    if (!this.cull) {
      return true;
    }
    if (this.cull?.type === 'ClientCull') {
      if (!(this.cull as ClientCullDTO)?.staticElements && !this.cull.atoms) {
        return true;
      }
      if ((this.cull as ClientCullDTO)?.staticElements && !this.cull.atoms) {
        return (this.cull as ClientCullDTO).staticElements.length <= 0;
      }
      if (!(this.cull as ClientCullDTO)?.staticElements && this.cull.atoms) {
        return this.clientCullAtomNotValid();
      }
      if ((this.cull as ClientCullDTO)?.staticElements && this.cull.atoms) {
        return (this.cull as ClientCullDTO).staticElements.length <= 0 && this.clientCullAtomNotValid();
      }
      return false;
    }
    if (this.cull?.type === 'ContentCull') {
      if (!(this.cull as ContentCullDTO)?.staticElements && !this.cull.atoms) {
        return true;
      }
      if ((this.cull as ContentCullDTO)?.staticElements && !this.cull.atoms) {
        return (this.cull as ContentCullDTO).staticElements.length <= 0;
      }
      if (!(this.cull as ContentCullDTO)?.staticElements && this.cull.atoms) {
        return this.contentCullAtomNotValid();
      }
      if ((this.cull as ContentCullDTO)?.staticElements && this.cull.atoms) {
        return (this.cull as ContentCullDTO).staticElements.length <= 0 && this.contentCullAtomNotValid();
      }
      return false;
    }
    if (this.cull?.type === 'PlayerCull') {
      if (!(this.cull as PlayerCullDTO)?.staticElements && !this.cull.atoms) {
        return true;
      }
      if ((this.cull as PlayerCullDTO)?.staticElements && !this.cull.atoms) {
        return (this.cull as PlayerCullDTO).staticElements.length <= 0;
      }
      if (!(this.cull as PlayerCullDTO)?.staticElements && this.cull.atoms) {
        return this.playerCullAtomNotValid();
      }
      if ((this.cull as PlayerCullDTO)?.staticElements && this.cull.atoms) {
        return (this.cull as PlayerCullDTO).staticElements.length <= 0 && this.playerCullAtomNotValid();
      }
      return false;
      // return (((this.cull as PlayerCullDTO)?.staticElements?.length > 0) || this.previewCull?.atoms?.length && this.cull?.expression);
    }
    return this.cull?.atoms?.length <= 0 || !this.cull?.expression || !this.expressionValid;
  }

  playerCullAtomNotValid(): boolean {
    // TODO refactor
    if (this.cull.atoms.length <= 0) {
      return true;
    } else {
      for (const atom of this.cull.atoms) {
        if (!atom.operator || atom.operator.toString() === '. . .') {
          return true;
        }
        if (atom.type !== 'PlayerByPlayerCullAtom') {
          const pAtom = atom as PlayerPathBasedStructureCullAtomDTO;
          if (!pAtom.field || pAtom.field === '. . .') {
            return true;
          }
          if (!pAtom.values || pAtom.values.length <= 0) {
            return true;
          }
        } else {
          const pAtom = atom as ChainedPlayerCullAtomDTO;
          if (!pAtom.chainedCull || atom.operator.toString() === '. . .') {
            return true;
          }
        }
      }
      if (!this.cull.expression) {
        return true;
      }
    }
    return false;
  }

  clientCullAtomNotValid(): boolean {
    // TODO refactor
    if (this.cull.atoms.length <= 0) {
      return true;
    } else {
      for (const atom of this.cull.atoms) {
        if (!atom.operator || atom.operator.toString() === '. . .') {
          return true;
        }
        if (atom.type !== 'ClientByClientCullAtom') {
          const pAtom = atom as ClientIdentityReturningPathBasedStructureCullAtomDTO;
          if (!pAtom.field || pAtom.field === '. . .') {
            return true;
          }
          if (!pAtom.values || pAtom.values.length <= 0) {
            return true;
          }
        } else {
          const pAtom = atom as ChainedClientCullAtomDTO;
          if (!pAtom.chainedCull || atom.operator.toString() === '. . .') {
            return true;
          }
        }
        if (atom.type !== 'ClientByClientCullAtom') {
          const pAtom = atom as ClientIdentityReturningPathBasedStructureCullAtomDTO;
          if (!pAtom.field || pAtom.field === '. . .') {
            return true;
          }
          if (!pAtom.values || pAtom.values.length <= 0) {
            return true;
          }
        } /*
        else {
          const pAtom = atom as ChainedClientCullAtomDTO;
          if (!pAtom.chainedCull || atom.operator.toString() === '. . .') {
            return true;
          }
        api update}
          */
      }
      if (!this.cull.expression) {
        return true;
      }
    }
    return false;
  }

  contentCullAtomNotValid(): boolean {
    // TODO refactor
    if (this.cull.atoms.length <= 0) {
      return true;
    } else {
      for (const atom of this.cull.atoms) {
        if (!atom.operator || atom.operator.toString() === '. . .') {
          return true;
        }
        if (atom.type !== 'ContentByContentCullAtom') {
          const pAtom = atom as ContentPathBasedStructureCullAtomDTO;
          if (!pAtom.field || pAtom.field === '. . .') {
            return true;
          }
          if (!pAtom.values || pAtom.values.length <= 0) {
            return true;
          }
        } else {
          const pAtom = atom as ChainedContentCullAtomDTO;
          if (!pAtom.chainedCull || atom.operator.toString() === '. . .') {
            return true;
          }
        }
      }
      if (!this.cull.expression) {
        return true;
      }
    }
    return false;
  }

  /* tabDisabled() {
     if (this.cull?.type === 'ClientCull') {
       return !((this.cull as ClientCullDTO)?.staticElements?.length);
     }
     if (this.cull?.type === 'PlayerCull') {
       return !(((this.cull as PlayerCullDTO)?.staticElements?.length > 0) || this.previewCull?.atoms?.length && this.cull?.expression);
     }
     return !this.form.valid || !this.cull?.atoms?.length || !this.cull?.expression;
   }

   submitDisabled() {
     if (this.cull?.type === 'ClientCull') {
       return !((this.cull as ClientCullDTO)?.staticElements?.length);
     }
     if (this.cull?.type === 'PlayerCull') {
       return !(((this.cull as PlayerCullDTO)?.staticElements?.length > 0) || this.previewCull?.atoms?.length && this.cull?.expression);
     }
     return !this.cull?.atoms?.length || !this.cull?.expression;
   }
 */

  previewComponent() {
    if (this.cull?.type === 'ClientCull') {
      return (this.cull as ClientCullDTO)?.staticElements?.length > 0 || (this.previewCull?.atoms?.length && this.cull?.expression);
    }
    if (this.cull?.type === 'PlayerCull') {
      return (this.cull as PlayerCullDTO)?.staticElements?.length > 0 || (this.previewCull?.atoms?.length && this.cull?.expression);
    }
    return this.previewCull?.atoms?.length && this.cull?.expression;
  }

  isCullExpressionValid() {
    return true;
    /*
   TODO check following commented out code

    let expression: string = '';

    const arr: string[] = [];

    if (this.cull && this.cull.atoms) {
      expression = this.cull.expression;

      for (const atom of this.cull.atoms) {
        if (atom?.id?.num) {
          arr.push(atom.id.num.toString());
        }
      }
    }

    const combinations: string[] = this.getCombinations(arr);

    try {
      let ret = false;

      for (const combi of combinations) {
        ret = LEP.parse(expression, t => combi.indexOf(t) > -1);

        if (ret) {
          break;
        }
      }

      if (ret) {
        for (let i = 0; i < expression.length; i++) {
          const curr: string = expression.charAt(i);
          let prev: string = null;

          if (i !== 0) {
            prev = expression.charAt(i - 1);
          }

          if (prev && curr === '(' && prev !== '&' && prev !== '|' && prev !== '(' && prev !== '!') {
            return false;
          }
        }

        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }

     */
  }

  private getCombinations(inputArray: any[]): any[] {
    const result = [];
    const f = (prefix: any = [], array: any) => {
      for (let i = 0; i < array.length; i++) {
        result.push([...prefix, array[i]]);
        f([...prefix, array[i]], array.slice(i + 1));
      }
    };
    f('', inputArray);
    return result;
  }

  refreshFromServer() {
    if (!this.cullId) {
      this.form.setValidators(this.nameValidator());
      this.cull = new CullWithAtomsDTO();
      if (this.type) {
        this.cull.type = this.type;
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_time_create])) {
        this.cull.type = 'TimeCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_player_create])) {
        this.cull.type = 'PlayerCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_client_create])) {
        this.cull.type = 'ClientCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_content_create])) {
        this.cull.type = 'ContentCull';
      } else if (this.permissionService.hasPermissions([this.permissionService.Permission.cull_geo_create]) && this.GEOCULLDONE) {
        this.cull.type = 'GeoCull';
      }

      this.cull.hidden = this.hidden;
      this.cull.easyMode = true;
      this.refreshEasyMode();
      this.loading = false;
    } else {
      this.api
        .cs_Cull_retrieveById(this.cullId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: data => {
            this.cull = data;
            this.color = '#' + data.color;

            this.refreshEasyMode();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  refreshEasyMode() {
    this.easyMode = this.cull.easyMode;
  }

  updateCull() {
    this.loading = true;
    this.cull.name = this.Name.value;
    this.cull.type = this.Type.value;
    this.cull.easyMode = this.easyMode;

    let obs: Observable<CullWithAtomsDTO> = null;

    if (!this.cull.id) {
      if (!this.companyId) {
        switch (this.cull?.type) {
          case 'TimeCull':
            obs = this.api.cs_Cull_createTimeCullClientScoped(this.cull as TimeCullDTO);
            break;
          case 'PlayerCull':
            obs = this.api.cs_Cull_createPlayerCullClientScoped(this.cull as PlayerCullDTO);
            break;
          case 'ClientCull':
            obs = this.api.cs_Cull_createClientCullClientScoped(this.cull as ClientCullDTO);
            break;
          case 'ContentCull':
            obs = this.api.cs_Cull_createContentCullClientScoped(this.cull as ContentCullDTO);
            break;
          case 'GeoCull':
            obs = this.api.cs_Cull_createGeoCullClientScoped(this.cull as GeoCullDTO);
            break;
          default:
            break;
        }
      } else {
        switch (this.cull?.type) {
          case 'TimeCull':
            obs = this.api.cs_Cull_createTimeCullCompanyScoped(this.companyId, this.cull as TimeCullDTO);
            break;
          case 'PlayerCull':
            obs = this.api.cs_Cull_createPlayerCullCompanyScoped(this.companyId, this.cull as PlayerCullDTO);
            break;
          case 'ClientCull':
            obs = this.api.cs_Cull_createClientCullCompanyScoped(this.companyId, this.cull as ClientCullDTO);
            break;
          case 'ContentCull':
            obs = this.api.cs_Cull_createContentCullCompanyScoped(this.companyId, this.cull as ContentCullDTO);
            break;
          case 'GeoCull':
            obs = this.api.cs_Cull_createGeoCullCompanyScoped(this.companyId, this.cull as GeoCullDTO);
            break;
          default:
            break;
        }
      }
    } else {
      switch (this.cull?.type) {
        case 'TimeCull':
          obs = this.api.cs_Cull_updateTimeCull(this.cull as TimeCullDTO);
          break;
        case 'PlayerCull':
          obs = this.api.cs_Cull_updatePlayerCull(this.cull as PlayerCullDTO);
          break;
        case 'ClientCull':
          obs = this.api.cs_Cull_updateClientCull(this.cull as ClientCullDTO);
          break;
        case 'ContentCull':
          obs = this.api.cs_Cull_updateContentCull(this.cull as ContentCullDTO);
          break;
        case 'GeoCull':
          obs = this.api.cs_Cull_updateGeoCull(this.cull as GeoCullDTO);
          break;
        default:
          break;
      }
    }

    if (obs) {
      obs.pipe(takeUntilDestroyed(this.destroyRef), take(1)).subscribe({
        next: data => {
          this.cull = data;
          this.submitSuccessful();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    }
  }

  submitSuccessful() {
    if (this.submitSuccess.observers.length > 0) {
      this.submitSuccess.emit(this.cull);
      return;
    }
    if (this.isInModalContext) {
      if (this.modalState.options.get(this.isInModalContext)) {
        this.modalState.modals.get(this.isInModalContext).close(this.cull);
      } else {
        this.modalNavigation.result(this.cull);
      }
    } else {
      this.navService.navigateToPoint(this.util.RoutingID.filters);
    }
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  selectCompany() {
    if (!this.isInModalContext) {
      this.modal
        .showNavigationModal({
          entryComponent: { value: CompaniesComponent, data: { toggleContext: 'hasCulls' } },
          title: null,
          modalOptions: { windowClass: 'bigXL' },
        })
        .then((result: CompanyDTO) => {
          if (result) {
            this.cull.company = result;
            this.companyId = result.id;
          } else {
            this.cull.company = null;
            this.companyId = null;
          }
        })
        .catch(() => {});
    } else {
      this.modalNavigation.navigate(CompaniesComponent, {});
    }
  }

  setValue(key: string, value: string) {
    if (!value) {
      return;
    }
    if (this.cull) {
      switch (key) {
        case 'name': {
          this.cull.name = value;
          break;
        }
        case 'type': {
          if (this.cull.type !== value) {
            const nameTmp = this.cull.name;
            const categoryTmp = this.cull.category;
            const colorTmp = this.cull.color;
            this.cull = new CullWithAtomsDTO();
            this.cull.name = nameTmp;
            this.cull.category = categoryTmp;
            this.cull.color = colorTmp;
            this.cull.type = value;
            this.cull.hidden = this.hidden;
            this.cull.easyMode = true;

            this.refreshEasyMode();
          } else {
            this.cull.type = value;
          }
          break;
        }
      }
    }
  }

  switchToAdvancedMode() {
    this.modal
      .confirm({
        title: this.translate.instant('lang.culls.switchToAdvancedMode'),
        message: this.translate.instant('lang.culls.switchToAdvancedModeQuestion'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.cull.easyMode = false;
          this.refreshEasyMode();
        }
      });
  }

  cancel() {
    if (this.submitSuccess.observers.length > 0) {
      this.submitSuccess.emit(null);
      return;
    }
    if (this.isInModalContext) {
      this.modalState.modals.get(this.isInModalContext).dismiss();
    } else {
      this.util.returnToNavigation(this.util.RoutingID.filters);
    }
  }

  selectCategory() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: CullCategoriesComponent, data: { company: this.cull?.company } },
        title: null,
        modalOptions: { windowClass: 'M' },
      })
      .then((result: CullCategoryDTO) => {
        if (result) {
          this.cull.category = result;
        } else {
          this.cull.category = null;
        }
      })
      .catch(() => {});
  }

  deleteCategory(): void {
    this.cull.category = null;
  }

  createCategory(): void {
    const category: CullCategoryDTO = new CullCategoryDTO();
    this.modal
      .showNavigationModal({
        entryComponent: { value: CullCategoryEditComponent, data: { cullCategory: category, company: this.cull.company } },
        title: null,
        modalOptions: { windowClass: 'M' },
      })
      .then((result: CullCategoryDTO) => {
        if (result) {
          this.api
            .ca_md_CullCategory_createCategory(result)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: (cat: CullCategoryDTO) => {
                this.cull.category = cat;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        } else {
          this.cull.category = null;
        }
      })
      .catch(() => {});
  }

  getPolygonsPreview() {
    if (this.previewCull.type !== 'GeoCull') {
      return;
    }
    let obs: Observable<CullWithAtomsDTO>;
    if (this.previewCull.id) {
      obs = this.api.cs_Cull_updateGeoCullPreview(this.previewCull as GeoCullDTO);
    } else if (this.previewCull.company) {
      obs = this.api.cs_Cull_createGeoCullCompanyScopedPreview(this.previewCull.company.id, this.previewCull as GeoCullDTO);
    } else {
      obs = this.api.cs_Cull_createGeoCullClientScopedPreview(this.previewCull as GeoCullDTO);
    }

    obs.subscribe({
      next: (cull: GeoCullDTO) => {
        this.polygonsToRender = cull.validPolygons;
      },
    });
  }

  setColor($event) {
    if ($event) {
      const temp = $event.split('#');
      this.cull.color = temp[temp.length - 1];
    }
  }

  cancelColor() {
    this.showColorPicker = false;
  }

  showColorPickerFn() {
    this.colorService.getColorSet();
    this.showColorPicker = true;
  }

  checkColorSavePermission(): boolean {
    return this.permissionService.hasPermission(this.permissionService.Permission.color_definition_create);
  }

  checkColorDeletePermission(): string {
    if (this.permissionService.hasPermission(this.permissionService.Permission.color_definition_delete)) {
      // return 'cp-remove-color-button-class';
    }
    return 'd-none';
  }

  stepThree(wizard: any, tab3: any) {
    if (!this.nextDisable) {
      this.nextStep(wizard, tab3);
      this.currentTab = 3;
      this.getPolygonsPreview();
    }
  }
}
